import React, { useEffect, useState } from 'react';
import { Award, Wrench, BookOpen, Headphones, Eye, GraduationCap, ArrowRight, Rocket, ChevronDown } from 'lucide-react';
import { Wrench as WrenchP, BarChart3, Package, Smartphone, Users, Bot, Settings } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import DealerApplyModal from './DealerApplyModal';

const benefitIcons = [Award, Wrench, BookOpen, Headphones, Eye, GraduationCap];
const benefitColors = ['#39ff14', '#00f0ff', '#ff2d95', '#ffe600', '#b026ff', '#39ff14'];
const pkgIconMap = { engine: WrenchP, chart: BarChart3, headset: Headphones, package: Package, mobile: Smartphone, people: Users, robot: Bot, settings2: Settings };
const pkgColors = ['#39ff14', '#00f0ff', '#ff2d95', '#ffe600', '#b026ff', '#39ff14', '#00f0ff', '#ff2d95'];

const DEALER_T = {
  en: {
    heroSub:      'Join the H2 ELEMENT global network',
    heroDesc:     'Become an official partner and build a profitable hydrogen technology business in your region. Exclusive territory, full support, proven system.',
    applyNow:     'APPLY NOW',
    learnMore:    'LEARN MORE',
    benefitsHead: 'Why partner with us',
    pkgHead1:     'H2 ELEMENT',
    pkgHead2:     'Dealer Package',
    requireHead:  'DEALER REQUIREMENTS',
    ctaTitle:     'Ready to start?',
    ctaDesc:      'Fill out the application and our team will contact you within 24 hours to discuss your region and opportunities.',
    ctaBtn:       'APPLY TO BECOME A DEALER',
  },
  es: {
    heroSub:      'Únete a la red global de H2 ELEMENT',
    heroDesc:     'Conviértete en socio oficial y construye un negocio rentable de tecnología de hidrógeno en tu región. Territorio exclusivo, soporte completo, sistema probado.',
    applyNow:     'SOLICITAR AHORA',
    learnMore:    'MÁS INFORMACIÓN',
    benefitsHead: 'Por qué asociarte con nosotros',
    pkgHead1:     'H2 ELEMENT',
    pkgHead2:     'Paquete de Distribuidor',
    requireHead:  'REQUISITOS DEL DISTRIBUIDOR',
    ctaTitle:     '¿Listo para empezar?',
    ctaDesc:      'Completa la solicitud y nuestro equipo se comunicará contigo en 24 horas para discutir tu región y oportunidades.',
    ctaBtn:       'SOLICITAR SER DISTRIBUIDOR',
  },
  he: {
    heroSub:      'הצטרף לרשת הגלובלית של H2 ELEMENT',
    heroDesc:     'הפוך לשותף רשמי ובנה עסק רווחי בטכנולוגיית מימן באזורך. טריטוריה בלעדית, תמיכה מלאה, מערכת מוכחת.',
    applyNow:     'הגש בקשה',
    learnMore:    'למידע נוסף',
    benefitsHead: 'למה לשתף פעולה איתנו',
    pkgHead1:     'H2 ELEMENT',
    pkgHead2:     'חבילת סוכן',
    requireHead:  'דרישות לסוכן',
    ctaTitle:     'מוכן להתחיל?',
    ctaDesc:      'מלא את הבקשה והצוות שלנו ייצור איתך קשר תוך 24 שעות כדי לדון באזורך ובהזדמנויות.',
    ctaBtn:       'הגש בקשה להיות סוכן',
  },
};

export default function DealerPage() {
  const { lang, t } = useLanguage();
  const d  = t.dealers;
  const dp = t.dealerPackage;
  const lt = DEALER_T[lang] || DEALER_T.en;

  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#39ff14]/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00f0ff]/5 rounded-full blur-[100px]" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-[10px] tracking-[0.5em] text-[#ffe600]/70 font-mono mb-4" style={{ fontFamily: 'Share Tech Mono' }}>
            {d.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron' }}>
            {d.title1} <span className="text-[#39ff14]" style={{ textShadow: '0 0 30px rgba(57,255,20,0.35)' }}>{d.title2}</span>
          </h1>
          <p className="text-[#00f0ff]/80 text-sm tracking-[0.2em] font-semibold mb-6" style={{ fontFamily: 'Share Tech Mono' }}>
            {lt.heroSub}
          </p>
          <p className="text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
            {lt.heroDesc}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <button
              onClick={() => setApplyOpen(true)}
              className="px-10 py-4 bg-[#39ff14] text-[#0a0a0f] font-bold text-xs tracking-[0.2em] inline-flex items-center gap-2 transition-all hover:shadow-[0_0_30px_rgba(57,255,20,0.3)] cursor-pointer"
              style={{ fontFamily: 'Orbitron' }}
            >
              <Rocket size={14} /> {lt.applyNow}
            </button>
            <button
              onClick={() => document.getElementById('dealer-benefits')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 border border-[#00f0ff]/30 text-[#00f0ff] font-bold text-xs tracking-[0.2em] inline-flex items-center gap-2 hover:border-[#00f0ff]/60 transition-all"
              style={{ fontFamily: 'Orbitron' }}
            >
              {lt.learnMore} <ChevronDown size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ──────────────────────────────────────────── */}
      <section className="py-20 relative" id="dealer-benefits">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#39ff14]/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-14" style={{ fontFamily: 'Orbitron' }}>
            {lt.benefitsHead}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.benefits.map((benefit, index) => {
              const Icon = benefitIcons[index] || Award;
              const color = benefitColors[index];
              return (
                <div key={index} className="group p-6 transition-all duration-500 hover:bg-white/[0.03]"
                     style={{ border: `1px solid ${color}12` }}>
                  <div className="w-12 h-12 flex items-center justify-center mb-4"
                       style={{ border: `1px solid ${color}30`, boxShadow: `0 0 12px ${color}15` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <h3 className="font-bold text-white text-sm mb-2" style={{ fontFamily: 'Orbitron', fontSize: '12px' }}>{benefit.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS (Steps) ──────────────────────────────── */}
      <section className="py-20 bg-[#0d0d1a] relative">
        <div className="absolute inset-0 cyber-stripes opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-black text-white text-center mb-12" style={{ fontFamily: 'Orbitron' }}>
            {d.howToTitle1} <span className="text-[#00f0ff]">{d.howToTitle2}</span> {d.howToTitle3}
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            {d.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="p-6 h-full" style={{ border: '1px solid rgba(0,240,255,0.1)', background: 'rgba(0,240,255,0.02)' }}>
                  <span className="text-5xl font-black text-[#00f0ff]/10" style={{ fontFamily: 'Orbitron' }}>{step.step}</span>
                  <h4 className="font-bold text-white/90 mt-3 mb-2 text-sm" style={{ fontFamily: 'Orbitron', fontSize: '11px' }}>{step.title}</h4>
                  {step.description && <p className="text-xs text-white/50">{step.description}</p>}
                </div>
                {index < d.steps.length - 1 && (
                  <div className="hidden md:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight size={14} className="text-[#00f0ff]/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEALER PACKAGE ────────────────────────────────────── */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-12" style={{ fontFamily: 'Orbitron' }}>
            {dp.title1} <span className="text-[#00f0ff]" style={{ textShadow: '0 0 15px rgba(0,240,255,0.3)' }}>{dp.title2}</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {dp.items.map((item, index) => {
              const Icon = pkgIconMap[item.icon] || WrenchP;
              const color = pkgColors[index];
              return (
                <div key={index} className="group p-5 transition-all duration-500 hover:bg-white/[0.02]"
                     style={{ border: `1px solid ${color}10` }}>
                  <div className="w-9 h-9 flex items-center justify-center mb-3"
                       style={{ border: `1px solid ${color}25` }}>
                    <Icon size={15} style={{ color }} />
                  </div>
                  <h3 className="font-bold text-white/95 text-xs mb-1" style={{ fontFamily: 'Orbitron', fontSize: '10px' }}>{item.title}</h3>
                  <p className="text-[11px] text-white/55 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── REQUIREMENTS ──────────────────────────────────────── */}
      <section className="py-16 bg-[#0d0d1a] relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-[#39ff14]/20 p-8 md:p-12"
               style={{ background: 'rgba(57,255,20,0.03)', boxShadow: '0 0 30px rgba(57,255,20,0.05)' }}>
            <h3 className="text-lg font-black text-[#39ff14] mb-8" style={{ fontFamily: 'Orbitron', fontSize: '14px' }}>{lt.requireHead}</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {d.requirements.map((req, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 border border-[#39ff14]/30">
                    <span className="text-[10px] font-bold text-[#39ff14]" style={{ fontFamily: 'Share Tech Mono' }}>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed">{req}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#39ff14]/[0.02] to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6" style={{ fontFamily: 'Orbitron' }}>
            {lt.ctaTitle}
          </h2>
          <p className="text-white/55 mb-10 leading-relaxed">
            {lt.ctaDesc}
          </p>
          <button
            onClick={() => setApplyOpen(true)}
            className="inline-flex items-center gap-3 px-12 py-5 bg-[#39ff14] text-[#0a0a0f] font-bold text-sm tracking-[0.2em] transition-all hover:shadow-[0_0_40px_rgba(57,255,20,0.35)] cursor-pointer"
            style={{ fontFamily: 'Orbitron' }}
          >
            <Rocket size={16} /> {lt.ctaBtn}
          </button>
        </div>
      </section>

      <Footer />

      {/* Apply modal */}
      <DealerApplyModal isOpen={applyOpen} onClose={() => setApplyOpen(false)} />
    </div>
  );
}
